import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useHabitsQuery } from '../../hooks/queries';
import {
  useCreateHabit,
  useUpdateHabit,
  useDeleteHabit,
  useCompleteHabit,
} from '../../hooks/mutations';
import { useDateTime } from '../../contexts/DateTimeContext';
import { useEntitlements } from '../../hooks/useEntitlements';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import TimeSelect from '../../components/ui/TimeSelect';
import ConfirmModal from '../../components/ConfirmModal';
import ReminderPicker from '../../components/task/pickers/ReminderPicker';
import { useViewing } from '../../lib/viewing';
import {
  Plus, Clock, Flame, Trash2,
  Edit2, X, Check, Target, Repeat, Zap, AlertTriangle
} from 'lucide-react';
import { APP_COLOR_VALUES } from '../../constants/colors';
import MobileFAB from '../../components/mobile/MobileFAB';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalTitle,
  ResponsiveModalClose,
  ResponsiveModalBody,
  ResponsiveModalFooter,
} from '../../components/ui/responsive-modal';

// Recurrences with a label (habits.<value>); anything else shows as stored.
const RECURRENCES = ['daily', 'weekdays', 'weekly', 'biweekly', 'monthly'];

const EMPTY_FORM = { title: '', duration: 30, recurrence: 'daily', times_per_period: 1, preferred_time: 'morning', preferred_time_specific: '', auto_schedule: true, color: '#F59E0B', reminders: [] };

function getConflictingHabits(formData, allHabits, editingHabitId) {
  if (!formData.preferred_time_specific) return [];
  const [newH, newM] = formData.preferred_time_specific.split(':').map(Number);
  const newStart = newH * 60 + newM;
  const newEnd = newStart + Number(formData.duration);
  return allHabits.filter(habit => {
    if (habit.id === editingHabitId) return false;
    if (!habit.preferred_time_specific) return false;
    const [h, m] = habit.preferred_time_specific.split(':').map(Number);
    const hStart = h * 60 + m;
    const hEnd = hStart + habit.duration;
    return newStart < hEnd && newEnd > hStart;
  });
}

// The backend stores `last_completed` as a naive UTC datetime, which FastAPI
// serializes without a timezone suffix. Normalize it so JS parses it as UTC,
// then compare against the user's local "today".
function isCompletedToday(habit) {
  if (!habit.last_completed) return false;
  let iso = habit.last_completed;
  if (typeof iso !== 'string') return false;
  if (!/(Z|[+-]\d{2}:?\d{2})$/i.test(iso)) iso += 'Z';
  const last = new Date(iso);
  if (Number.isNaN(last.getTime())) return false;
  const now = new Date();
  return (
    last.getFullYear() === now.getFullYear() &&
    last.getMonth() === now.getMonth() &&
    last.getDate() === now.getDate()
  );
}

const HabitsPage = () => {
  const { t } = useTranslation();
  const { timeFormat, dateLocale } = useDateTime();
  const { limitOf } = useEntitlements();
  const habitsQuery = useHabitsQuery();
  const habits = habitsQuery.data || [];
  const loading = habitsQuery.isPending;
  const createHabit = useCreateHabit();
  const updateHabit = useUpdateHabit();
  const deleteHabit = useDeleteHabit();
  const completeHabit = useCompleteHabit();
  const [showModal, setShowModal] = useState(false);
  const [editingHabit, setEditingHabit] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, habitId: null });
  const [formData, setFormData] = useState(EMPTY_FORM);
  // A habit's link (/app/habits?habit=<id>, its reminder): shown, and its reminder settled
  // (backend utils/habits.py) while it is in front of the person.
  const [searchParams] = useSearchParams();
  const linkedHabitId = searchParams.get('habit');
  const linkedId = linkedHabitId && habits.some((h) => h.id === linkedHabitId) ? linkedHabitId : null;
  useViewing(linkedId ? `habit:${linkedId}` : null);
  useEffect(() => {
    if (linkedId) document.getElementById(`habit-${linkedId}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [linkedId]);
  const conflictingHabits = showModal
    ? getConflictingHabits(formData, habits, editingHabit?.id)
    : [];

  // Legacy/invalid habits may have a blank title (created before backend
  // validation existed). Never surface an empty name in the conflict warning.
  const habitDisplayName = (habit) => {
    const title = (habit.title || '').trim();
    return title || t('habits.untitled_habit');
  };

  useEffect(() => {
    if (!showModal) return;
    const handleEscape = (e) => {
      if (e.key === 'Escape') { setShowModal(false); setEditingHabit(null); }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showModal]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const title = (formData.title || '').trim();
    // Native `required` blocks an empty field; this guards whitespace-only
    // titles so a blank-name habit can never be created from the UI.
    if (!title) return;
    try {
      const payload = {
        ...formData,
        title,
        preferred_time_specific: formData.preferred_time_specific || null,
      };
      if (editingHabit) {
        await updateHabit.mutateAsync({ id: editingHabit.id, data: payload });
        toast.success(t('habits.habit_updated'));
      } else {
        await createHabit.mutateAsync(payload);
        toast.success(t('habits.habit_created'));
      }
      setShowModal(false);
      setEditingHabit(null);
      setFormData(EMPTY_FORM);
    } catch (error) {
      console.error('Error saving habit:', error);
    }
  };

  const handleComplete = (habitId) => {
    completeHabit.mutate(habitId);
  };

  const handleDelete = (habitId) => {
    setConfirmModal({ isOpen: true, habitId });
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteHabit.mutateAsync(confirmModal.habitId);
      toast.success(t('habits.habit_deleted'));
    } catch (error) {
      console.error('Error deleting habit:', error);
    } finally {
      setConfirmModal({ isOpen: false, habitId: null });
    }
  };

  const openEditModal = (habit) => {
    setEditingHabit(habit);
    setFormData({
      title: habit.title,
      duration: habit.duration,
      recurrence: habit.recurrence,
      times_per_period: habit.times_per_period,
      preferred_time: habit.preferred_time || 'morning',
      preferred_time_specific: habit.preferred_time_specific || '',
      auto_schedule: habit.auto_schedule !== false,
      color: habit.color,
      reminders: habit.reminders || [],
    });
    setShowModal(true);
  };

  const colors = APP_COLOR_VALUES;

  const openNewHabit = () => {
    const cap = limitOf('habits');
    if (cap !== null && habits.length >= cap) {
      window.dispatchEvent(new CustomEvent('upgradNeeded', {
        detail: { resource: 'habits', message: t('limits.habits_limit', { count: cap, defaultValue: `Your Free plan allows ${cap} habits. Upgrade for more.` }) },
      }));
      return;
    }
    setEditingHabit(null);
    setFormData(EMPTY_FORM);
    setShowModal(true);
  };

  const formatTime = (habit) => {
    if (habit.preferred_time_specific) {
      if (timeFormat === '12h') {
        const [h, m] = habit.preferred_time_specific.split(':').map(Number);
        return format(new Date(2000, 0, 1, h, m), 'h:mm a', { locale: dateLocale });
      }
      return habit.preferred_time_specific;
    }
    const labels = {
      morning: t('habits.morning_label'),
      afternoon: t('habits.afternoon_label'),
      evening: t('habits.evening_label'),
      any: t('habits.any_label')
    };
    return labels[habit.preferred_time] || habit.preferred_time;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="hidden md:block text-lg font-bold text-foreground">{t('habits.title')}</h1>
          <p className="text-muted-foreground text-xs md:mt-1">{t('habits.subtitle')}</p>
        </div>
        <Button data-tour="new-habit" onClick={openNewHabit} className="hidden md:inline-flex bg-amber-500 text-black hover:bg-amber-400">
          <Plus className="w-4 h-4 mr-2" />
          {t('habits.add_habit')}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/15 rounded-lg">
              <Target className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">{t('habits.active_habits')}</p>
              <p className="text-2xl font-bold text-foreground">{habits.filter(h => h.is_active).length}</p>
            </div>
          </div>
        </div>
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/15 rounded-lg">
              <Flame className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">{t('habits.total_streak')}</p>
              <p className="text-2xl font-bold text-foreground">
                {habits.reduce((acc, h) => acc + (h.streak || 0), 0)} {t('habits.days')}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-card rounded-xl border border-border p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-500/15 rounded-lg">
              <Check className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="text-muted-foreground text-sm">{t('habits.completed')}</p>
              <p className="text-2xl font-bold text-foreground">
                {habits.reduce((acc, h) => acc + (h.total_completed || 0), 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Habits Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {habits.map((habit) => (
          <div
            key={habit.id}
            id={`habit-${habit.id}`}
            className={`bg-card rounded-xl border p-5 hover:border-muted-foreground/40 transition-colors group ${
              habit.id === linkedHabitId ? 'border-amber-500 ring-2 ring-amber-500/30' : 'border-border'
            }`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${habit.color}20` }}
                >
                  <Clock className="w-5 h-5" style={{ color: habit.color }} />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-foreground text-sm font-semibold">{habit.title}</h3>
                    {habit.streak > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-500/15 text-amber-500">
                        🔥 {habit.streak}
                      </span>
                    )}
                  </div>
                  <p className="text-muted-foreground text-xs">{habit.duration} {t('common.min')} · {formatTime(habit)}</p>
                </div>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => openEditModal(habit)}
                  className="p-1.5 hover:bg-accent rounded text-muted-foreground hover:text-foreground"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(habit.id)}
                  className="p-1.5 hover:bg-red-500/10 rounded text-muted-foreground hover:text-red-400"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
              <span className="flex items-center gap-1">
                <Repeat className="w-4 h-4" />
                {RECURRENCES.includes(habit.recurrence) ? t(`habits.${habit.recurrence}`) : habit.recurrence}
              </span>
              <span className="flex items-center gap-1">
                <Flame className="w-4 h-4 text-amber-500" />
                {habit.streak || 0} {t('habits.streak')}
              </span>
              {habit.auto_schedule && (
                <span className="flex items-center gap-1 text-amber-500">
                  <Zap className="w-3.5 h-3.5" />
                  {t('habits.auto_label')}
                </span>
              )}
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-border">
              <span className="text-muted-foreground text-xs">
                {t('habits.total_completed', { count: habit.total_completed || 0 })}
              </span>
              {isCompletedToday(habit) ? (
                <Button
                  size="sm"
                  disabled
                  className="bg-amber-500/20 text-amber-500 cursor-default"
                >
                  <Check className="w-4 h-4 mr-1" />
                  {t('habits.completed_today')}
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={() => handleComplete(habit.id)}
                  disabled={completeHabit.isPending}
                  className="bg-amber-500 text-black hover:bg-amber-400"
                >
                  <Check className="w-4 h-4 mr-1" />
                  {t('common.done')}
                </Button>
              )}
            </div>
          </div>
        ))}

        {habits.length === 0 && !loading && (
          <div className="col-span-full text-center py-12">
            <Clock className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-foreground text-sm font-semibold mb-2">{t('habits.no_habits')}</h3>
            <p className="text-muted-foreground text-xs mb-4">{t('habits.no_habits_description')}</p>
            <Button onClick={openNewHabit} className="bg-amber-500 text-black hover:bg-amber-400">
              <Plus className="w-4 h-4 mr-2" />
              {t('habits.add_habit')}
            </Button>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <ResponsiveModal
        open={showModal}
        onOpenChange={(open) => { if (!open) { setShowModal(false); setEditingHabit(null); } }}
      >
        <ResponsiveModalContent className="md:rounded-xl" overlayClassName="bg-black/80">
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>
              {editingHabit ? t('habits.edit_habit') : t('habits.new_habit')}
            </ResponsiveModalTitle>
            <ResponsiveModalClose />
          </ResponsiveModalHeader>

          <form
            id="habit-form"
            onSubmit={handleSubmit}
            className="flex flex-col flex-1 min-h-0"
          >
            <ResponsiveModalBody className="space-y-4 p-6">
              <div>
                <Label className="text-muted-foreground text-xs">{t('habits.habit_name')}</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="mt-1.5 bg-muted/40 border-border text-foreground"
                  placeholder={t('habits.habit_name_placeholder')}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground text-xs">{t('habits.duration_minutes')}</Label>
                  <Input
                    type="number"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                    className="mt-1.5 bg-muted/40 border-border text-foreground"
                    min={5}
                    max={240}
                  />
                </div>
                <div>
                  <Label className="text-muted-foreground text-xs">{t('habits.recurrence')}</Label>
                  <select
                    value={formData.recurrence}
                    onChange={(e) => setFormData({ ...formData, recurrence: e.target.value })}
                    className="mt-1.5 w-full px-3 py-2 bg-muted/40 border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="daily">{t('habits.daily')}</option>
                    <option value="weekdays">{t('habits.weekdays')}</option>
                    <option value="weekly">{t('habits.weekly')}</option>
                    <option value="biweekly">{t('habits.biweekly')}</option>
                    <option value="monthly">{t('habits.monthly')}</option>
                  </select>
                </div>
              </div>

              {/* Preferred time */}
              <div>
                <Label className="text-muted-foreground text-xs">{t('habits.preferred_time')}</Label>
                <div className="mt-1.5 grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">{t('habits.exact_time_optional')}</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <TimeSelect
                          value={formData.preferred_time_specific}
                          onChange={(val) => setFormData({ ...formData, preferred_time_specific: val })}
                          placeholder={t('habits.no_exact_time')}
                          step={15}
                        />
                      </div>
                      {/* BG_40: allow deselecting the optional exact time so the
                          user can fall back to a time-of-day selection. */}
                      {formData.preferred_time_specific && (
                        <button
                          type="button"
                          onClick={() => setFormData({ ...formData, preferred_time_specific: '' })}
                          title={t('habits.clear_exact_time', { defaultValue: 'Clear exact time' })}
                          aria-label={t('habits.clear_exact_time', { defaultValue: 'Clear exact time' })}
                          className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors flex-shrink-0"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">{t('habits.or_time_of_day')}</p>
                    <select
                      value={formData.preferred_time}
                      onChange={(e) => setFormData({ ...formData, preferred_time: e.target.value })}
                      className={`w-full px-3 py-2 bg-muted/40 border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-amber-500 ${formData.preferred_time_specific ? 'opacity-40' : ''}`}
                      disabled={!!formData.preferred_time_specific}
                    >
                      <option value="morning">{t('habits.morning_time')}</option>
                      <option value="afternoon">{t('habits.afternoon_time')}</option>
                      <option value="evening">{t('habits.evening_time')}</option>
                      <option value="any">{t('habits.any_time')}</option>
                    </select>
                  </div>
                </div>
                {formData.preferred_time_specific && (
                  <p className="text-xs text-amber-500 mt-1">{t('habits.exact_time_overrides')}</p>
                )}
              </div>

              {/* Reminders before the habit's time, as a task has them */}
              <div className="flex items-center justify-between">
                <Label className="text-muted-foreground text-xs">{t('tasks.reminders_label')}</Label>
                <ReminderPicker
                  value={formData.reminders || []}
                  onChange={(reminders) => setFormData({ ...formData, reminders })}
                />
              </div>

              {/* Auto-schedule toggle */}
              <div className="flex items-center justify-between py-2 px-3 bg-muted/40 rounded-lg border border-border">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-500" />
                  <div>
                    <p className="text-foreground text-sm font-medium">{t('habits.auto_schedule')}</p>
                    <p className="text-muted-foreground text-xs">{t('habits.auto_schedule_desc')}</p>
                  </div>
                </div>
                <Switch
                  checked={formData.auto_schedule}
                  onCheckedChange={(v) => setFormData({ ...formData, auto_schedule: v })}
                />
              </div>

              <div>
                <Label className="text-muted-foreground text-xs">{t('common.color')}</Label>
                <div className="flex gap-2 mt-2">
                  {colors.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormData({ ...formData, color })}
                      className={`w-8 h-8 rounded-lg transition-transform ${
                        formData.color === color ? 'scale-110 ring-2 ring-white' : ''
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              {conflictingHabits.length > 0 && (
                <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-500">
                    <span className="font-medium">{t('habits.time_conflict')}</span>{' '}
                    {conflictingHabits.length === 1
                      ? <>{t('habits.time_conflict_single', { title: habitDisplayName(conflictingHabits[0]) })}</>
                      : <>{t('habits.time_conflict_multiple', { titles: conflictingHabits.map(h => `"${habitDisplayName(h)}"`).join(', ') })}</>
                    }
                  </div>
                </div>
              )}

            </ResponsiveModalBody>
          </form>

          <ResponsiveModalFooter className="gap-3 justify-stretch px-6">
            <Button
              type="button"
              variant="outline"
              className="flex-1 border-border text-muted-foreground text-xs hover:bg-muted/40"
              onClick={() => { setShowModal(false); setEditingHabit(null); }}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" form="habit-form" className="flex-1 bg-amber-500 text-black hover:bg-amber-400">
              {editingHabit ? t('common.update') : t('common.create')}
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, habitId: null })}
        onConfirm={handleDeleteConfirm}
        title={t('habits.delete_habit_title')}
        message={t('habits.delete_habit_message')}
        confirmLabel={t('common.delete')}
        danger
      />

      <MobileFAB onClick={openNewHabit} label={t('habits.add_habit')} />
    </div>
  );
};

export default HabitsPage;
